<template>
  <div>
    <StateBlock v-if="!loaded && loading" state="loading" />
    <StateBlock v-else-if="error" state="error" @retry="load" />
    <StateBlock v-else-if="!candidates.length && !filtersActive" state="empty" title="Кандидатов пока нет" />

    <v-data-table
      v-else
      :headers="headers"
      :items="candidates"
      :loading="loading"
      :server-items-length="count"
      :options.sync="options"
      :items-per-page="limit || 25"
      :hide-default-footer="!!limit"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100], 'items-per-page-text': 'На странице:' }"
      disable-sort
      no-data-text="Кандидатов не найдено"
    >
      <!-- Фильтры по столбцам, как в общем списке кандидатов -->
      <template v-if="!limit" v-slot:body.prepend>
        <tr class="filters-row">
          <td class="pa-2">
            <v-text-field
              v-model="filter.name" label="Фамилия и Имя" append-icon="mdi-magnify" class="font-sm"
              dense hide-details outlined clearable @input="onFilter"
            />
          </td>
          <td class="pa-2">
            <v-text-field
              v-model="filter.email" label="Электронная почта" append-icon="mdi-magnify" class="font-sm"
              dense hide-details outlined clearable @input="onFilter"
            />
          </td>
          <td class="pa-2">
            <v-select
              v-model="filter.manager" :items="senderOptions" label="Отправил" class="font-sm"
              dense hide-details outlined clearable @change="onFilter"
            />
          </td>
          <td class="pa-2">
            <v-select
              v-model="filter.testing_status" :items="statusOptions" label="Статус тестирования" class="font-sm"
              dense hide-details outlined clearable @change="onFilter"
            />
          </td>
        </tr>
      </template>
      <template v-slot:item.name="{ item }">
        <router-link v-if="$store.getters['auth/isAllowed']('view_candidate')" class="text-primary" :to="{ name: 'candidates-id', params: { id: item.id } }">
          {{ fullName(item) }}
        </router-link>
        <span v-else>{{ fullName(item) }}</span>
      </template>
      <template v-slot:item.sender="{ item }">{{ senderName(item) }}</template>
      <template v-slot:item.status="{ item }">{{ statusTitle(item.testing_status) }}</template>
    </v-data-table>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { TESTING_STATUSES_TITLES } from '@/common/constants/testingStatuses';

// Кандидаты корпа и его менеджеров с колонкой «Отправил».
// Список берём из /users/candidates/?corp_client=ID: сервер отдаёт кандидатов корпа через его менеджеров,
// включая служебного менеджера (его кандидаты подписываются названием компании).
export default {
  name: 'ClientCandidates',
  components: { StateBlock },
  props: {
    corpId: { type: [Number, String], required: true },
    company: { type: String, default: '' },
    // Менеджеры корпа (id, name) — чтобы подписать «Отправил»
    managers: { type: Array, default: () => [] },
    // Короткий список (для «Обзора»): без пагинации
    limit: { type: Number, default: 0 },
  },
  data() {
    return {
      requestId: 0,
      candidates: [],
      count: 0,
      loading: false,
      loaded: false,
      error: false,
      filter: {
        name: '', email: '', manager: null, testing_status: null,
      },
      options: { page: 1, itemsPerPage: this.limit || 25 },
    };
  },
  computed: {
    filtersActive() {
      return Object.values(this.filter).some((v) => !!v);
    },
    senderOptions() {
      return this.managers.map((m) => ({ value: m.id, text: m.name }));
    },
    statusOptions() {
      return Object.entries(TESTING_STATUSES_TITLES).map(([value, text]) => ({ value, text }));
    },
    headers() {
      const headers = [
        { text: 'ФИО', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Отправил', value: 'sender' },
      ];
      if (!this.limit) headers.push({ text: 'Статус тестирования', value: 'status' });
      return headers;
    },
  },
  watch: {
    // Переход между карточками корпов без пересоздания компонента
    corpId() {
      this.candidates = [];
      this.count = 0;
      this.loaded = false;
      this.filter = {
        name: '', email: '', manager: null, testing_status: null,
      };
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    options: {
      handler(val, old) {
        if (!old || val.page !== old.page || val.itemsPerPage !== old.itemsPerPage) this.load();
      },
      deep: true,
    },
  },
  methods: {
    async load() {
      this.requestId += 1;
      const { requestId } = this;
      this.loading = true;
      this.error = false;
      try {
        const params = {
          corp_client: this.corpId,
          page: this.limit ? 1 : this.options.page,
          page_size: this.limit || this.options.itemsPerPage,
        };
        // Пустые фильтры в запрос не отправляем
        Object.entries(this.filter).forEach(([key, value]) => {
          if (value) params[key] = value;
        });
        const response = await api.get('/users/candidates/', { params });
        // Ответ на устаревший запрос (фильтр успели сменить) не применяем
        if (requestId !== this.requestId) return;
        this.candidates = Array.isArray(response) ? response : (response.results || []);
        this.count = Array.isArray(response) ? response.length : (response.count || 0);
      } catch (e) {
        if (requestId === this.requestId) this.error = true;
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
          this.loaded = true;
        }
      }
    },
    // Любой фильтр возвращает на первую страницу
    applyFilter() {
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    statusTitle(status) {
      return (status && TESTING_STATUSES_TITLES[status]) || status || '—';
    },
    fullName(item) {
      return [item.last_name, item.first_name].filter(Boolean).join(' ') || 'Имя не задано';
    },
    senderName(item) {
      const { manager } = item;
      if (!manager || (typeof manager === 'object' && manager.is_auxiliary)) return this.company || 'Компания';
      if (typeof manager === 'object') {
        return [manager.last_name, manager.first_name].filter(Boolean).join(' ') || 'Менеджер';
      }
      const m = this.managers.find((x) => String(x.id) === String(manager));
      return m ? m.name : 'Менеджер';
    },
  },
  created() {
    // Свой таймер у каждого экземпляра: поле ввода ждёт паузу, чтобы не слать запрос на каждую букву
    this.onFilter = debounce(this.applyFilter, 400);
    this.load();
  },
  beforeDestroy() {
    this.onFilter.cancel();
  },
};
</script>
